import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Selection } from '../types';
import TimeGrid from '../components/TimeGrid';
import NameModal from '../components/NameModal';
import { apiClient, APIError } from '../utils/apiClient';

const SessionPage: React.FC = () => {
  // Get sessionId from URL parameters
  const { id: sessionId } = useParams<{ id: string }>();
  
  // Load userName from localStorage
  const [userName, setUserName] = useState<string | null>(() => {
    return localStorage.getItem('userName');
  });
  
  // State for selections and showNameModal
  const [selections, setSelections] = useState<Selection[]>([]);
  const [showNameModal, setShowNameModal] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch selections from API
  const fetchSelections = async () => {
    if (!sessionId) {
      setError('Session ID is missing');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiClient.getSession(sessionId);
      setSelections(response.selections);
      setError(null);
    } catch (err) {
      if (err instanceof APIError) {
        if (err.status === 404) {
          setError('Сессия не найдена. Проверьте правильность ссылки.');
        } else if (err.status === 500) {
          setError('Ошибка сервера. Попробуйте позже.');
        } else {
          setError('Произошла ошибка при загрузке данных.');
        }
      } else {
        setError('Проблема с подключением. Проверьте интернет-соединение.');
      }
      console.error('Error fetching session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch selections on component mount and set up polling
  useEffect(() => {
    // Initial fetch
    fetchSelections();

    // Set up polling interval (every 5 seconds)
    const pollInterval = setInterval(() => {
      // Only poll if not in error state
      if (!error) {
        fetchSelections();
      }
    }, 5000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [sessionId, error]);

  // Loading state
  if (isLoading) {
    return (
      <div className="session-page">
        <div className="session-container flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Загрузка...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="session-page">
        <div className="session-container flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ошибка</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => {
                setIsLoading(true);
                setError(null);
                fetchSelections();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle slot click
  const handleSlotClick = async (timeSlot: string) => {
    // Check if userName exists
    if (!userName) {
      setShowNameModal(true);
      return;
    }

    if (!sessionId) {
      return;
    }

    // Check if this slot is already selected by current user
    const currentUserSelection = selections.find(s => s.userName === userName);
    const isSelected = currentUserSelection?.timeSlots.includes(timeSlot);

    try {
      if (isSelected) {
        // Optimistic UI update - remove selection
        setSelections(prevSelections =>
          prevSelections.map(selection =>
            selection.userName === userName
              ? {
                  ...selection,
                  timeSlots: selection.timeSlots.filter(slot => slot !== timeSlot)
                }
              : selection
          ).filter(selection => selection.timeSlots.length > 0) // Remove user if no slots left
        );

        // Send DELETE request
        await apiClient.deleteSelection({
          sessionId,
          userName,
          timeSlot
        });
      } else {
        // Optimistic UI update - add selection
        setSelections(prevSelections => {
          const existingUserIndex = prevSelections.findIndex(s => s.userName === userName);
          
          if (existingUserIndex >= 0) {
            // User already exists, add slot
            const updated = [...prevSelections];
            updated[existingUserIndex] = {
              ...updated[existingUserIndex],
              timeSlots: [...updated[existingUserIndex].timeSlots, timeSlot]
            };
            return updated;
          } else {
            // New user, create entry with assigned color
            const colorIndex = prevSelections.length % 8;
            const colors = [
              '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
              '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
            ];
            return [
              ...prevSelections,
              {
                userName,
                timeSlots: [timeSlot],
                color: colors[colorIndex]
              }
            ];
          }
        });

        // Send POST request
        await apiClient.addSelection({
          sessionId,
          userName,
          timeSlot
        });
      }

      // Fetch updated data to ensure consistency
      await fetchSelections();
    } catch (err) {
      console.error('Error updating selection:', err);
      // Revert optimistic update by fetching fresh data
      await fetchSelections();
      
      // Show error message
      if (err instanceof APIError) {
        alert('Не удалось обновить выбор. Попробуйте еще раз.');
      } else {
        alert('Проблема с подключением. Проверьте интернет-соединение.');
      }
    }
  };

  // Get the current URL for og:url
  const currentUrl = window.location.href;
  
  // Get the API base URL (assuming worker is on same domain or configured)
  const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin;
  const ogImageUrl = `${apiBaseUrl}/og-image/${sessionId}`;
  
  // Create dynamic title and description
  const pageTitle = selections.length > 0
    ? `Time Scheduler - ${selections.length} ${selections.length === 1 ? 'участник' : 'участника'}`
    : 'Time Scheduler - Выберите время';
  
  const pageDescription = selections.length > 0
    ? `${selections.map(s => s.userName).join(', ')} выбрали время для встречи. Присоединяйтесь!`
    : 'Выберите удобное время для встречи. Быстрое согласование без регистрации.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph meta tags */}
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      
      <div className="session-page">
        <div className="session-container">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Выберите удобное время
          </h1>
          
          <TimeGrid
            selections={selections}
            onSlotClick={handleSlotClick}
            currentUser={userName}
          />
          
          {showNameModal && (
            <NameModal
              onSubmit={(name) => {
                setUserName(name);
                setShowNameModal(false);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SessionPage;
